import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { formatApiError } from "../lib/api";
import { AuthShell } from "./Login";

export default function Signup() {
  const { t } = useTranslation();
  const { signup } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("auth:signup.pw_too_short", "La contraseña debe tener al menos 6 caracteres"));
      return;
    }
    setBusy(true);
    try {
      const result = await signup(email, password, name);
      if (result?.requires_verification) {
        toast.message(t("auth:signup.check_inbox", "Revisá tu correo para el código de 6 dígitos"));
        navigate(`/verify-email?email=${encodeURIComponent(result.email)}`);
        return;
      }
      toast.success(t("auth:signup.welcome", { app: branding.app_name || "Faro", defaultValue: "¡Bienvenido a {{app}}!" }));
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("auth:signup.failed", "No pudimos crear tu cuenta"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t("auth:signup.title", { app: branding.app_name || "Faro" })}
      subtitle={t("auth:signup.subtitle", { app: branding.app_name || "Faro" })}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
        <div>
          <Label htmlFor="name" className="text-xs uppercase tracking-wider font-mono">
            {t("auth:signup.name")}
          </Label>
          <Input
            id="name"
            data-testid="signup-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ada Lovelace"
            className="mt-1.5 h-11 rounded-sm"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono">
            {t("auth:login.email")}
          </Label>
          <Input
            id="email"
            type="email"
            required
            data-testid="signup-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="mt-1.5 h-11 rounded-sm"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs uppercase tracking-wider font-mono">
            {t("auth:login.password")}
          </Label>
          <Input
            id="password"
            type="password"
            required
            data-testid="signup-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth:signup.pw_placeholder", "Al menos 6 caracteres")}
            className="mt-1.5 h-11 rounded-sm"
          />
        </div>
        <Button type="submit" disabled={busy} data-testid="signup-submit-button" className="w-full h-11 rounded-sm">
          {busy ? t("auth:signup.submitting", "Creando cuenta…") : t("auth:signup.submit")}
        </Button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground">
        {t("auth:signup.have_account")}{" "}
        <Link to="/login" className="text-primary underline" data-testid="goto-login">
          {t("auth:signup.sign_in")}
        </Link>
      </div>
    </AuthShell>
  );
}
